import {
  ClientMessage,
  ServerMessage,
  type AddBotMsg,
  type BotErrorEvent,
  type BotTier,
  type CharacterId,
  type GameMode,
  type JoinOptions,
  type SettingsMsg,
} from '@arena/shared';
import { preloadMatchScreen } from '../app/matchChunk';
import { useSessionStore } from '../store/sessionStore';
import { useSettingsStore } from '../store/settingsStore';
import { bindMatch } from './bridge';
import {
  createArena,
  joinArenaById,
  joinOrCreateArena,
  reconnectArena,
  type ArenaRoom,
} from './client';

/** Survives a page reload in the same tab, so a refresh mid-match rejoins the same player. */
const TOKEN_KEY = 'arena.reconnectionToken';

let unbind: (() => void) | null = null;

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

function adopt(room: ArenaRoom): void {
  sessionStorage.setItem(TOKEN_KEY, room.reconnectionToken);
  unbind?.();
  unbind = bindMatch(room);

  room.onMessage(ServerMessage.BotError, (ev: BotErrorEvent) => {
    if (ev.reason === 'unavailable') useSessionStore.setState({ botsUnavailable: true });
  });
  // Rendered as a kill-feed notice by the lobby re-layout (DROW-119 C4); registered so the SDK
  // doesn't warn about an unhandled message until then.
  room.onMessage(ServerMessage.BotMadeRoom, () => {});
  room.onDrop(() => useSessionStore.setState({ status: 'reconnecting' }));
  room.onReconnect(() => {
    sessionStorage.setItem(TOKEN_KEY, room.reconnectionToken);
    useSessionStore.setState({ status: 'connected' });
  });
  room.onLeave(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    unbind?.();
    unbind = null;
    if (useSessionStore.getState().room === room) {
      useSessionStore.setState({ room: null, status: 'idle' });
    }
  });

  // A new room is a fresh chance: the server may have been restarted with a fixed bot config.
  useSessionStore.setState({ room, status: 'connected', error: null, botsUnavailable: false });
}

async function connect(join: () => Promise<ArenaRoom>): Promise<void> {
  if (useSessionStore.getState().status === 'connecting') return;
  useSessionStore.setState({ status: 'connecting', error: null });
  preloadMatchScreen();
  try {
    adopt(await join());
  } catch (err) {
    useSessionStore.setState({ status: 'idle', error: errorMessage(err) });
  }
}

/** Options every fresh join sends. A reconnect reuses its seat, so it doesn't need them. */
export function playerOptions(name: string): JoinOptions {
  return { name, character: useSettingsStore.getState().preferredCharacter };
}

/**
 * Lobby pick: remember it for future joins and ask the server to switch. The server decides
 * (waiting, not ready, pickable); the picker highlights whatever it syncs back.
 */
export function pickCharacter(room: ArenaRoom, character: CharacterId): void {
  useSettingsStore.getState().setPreferredCharacter(character);
  room.send(ClientMessage.Character, { character });
}

export const quickPlay = (name: string) => connect(() => joinOrCreateArena(playerOptions(name)));

export const createMatch = (name: string, isPrivate: boolean) =>
  connect(() => createArena({ ...playerOptions(name), private: isPrivate }));

export const joinByCode = (roomId: string, name: string) =>
  connect(() => joinArenaById(roomId.trim(), playerOptions(name)));

/** Shorter match for practice vs bots; the host can still change it in the waiting room. */
export const PRACTICE_SETTINGS = { rounds: 5, shopSeconds: 20 } as const satisfies SettingsMsg;

export interface PracticeOptions {
  count: number;
  tier: BotTier;
  mode: GameMode;
}

/**
 * Play vs Bots: a private room with practice settings, then the host asks for the bots. The
 * server spawns them; they join and ready up on their own like any other player.
 */
export const playVsBots = (name: string, { count, tier, mode }: PracticeOptions) =>
  connect(async () => {
    const room = await createArena({ ...playerOptions(name), private: true });
    room.send(ClientMessage.Settings, { ...PRACTICE_SETTINGS, mode });
    const addBot: AddBotMsg = { tier, count };
    // Typed against the shared payload: the server's typed room has no `addBot` handler until
    // B2 (DROW-91) lands, after which this can be a plain `room.send`.
    (room as unknown as { send(type: string, msg: unknown): void }).send(
      ClientMessage.AddBot,
      addBot,
    );
    return room;
  });

let resuming: Promise<void> | null = null;

/**
 * Rejoin the room this tab was in before a reload. Gives up quietly if the seat expired.
 * Idempotent: a token can only be redeemed once, and StrictMode runs mount effects twice.
 */
export function resumeSession(): Promise<void> {
  resuming ??= (async () => {
    const token = sessionStorage.getItem(TOKEN_KEY);
    if (!token) return;
    useSessionStore.setState({ status: 'connecting', error: null });
    preloadMatchScreen();
    try {
      adopt(await reconnectArena(token));
    } catch (err) {
      console.warn('Could not rejoin the previous match:', errorMessage(err));
      sessionStorage.removeItem(TOKEN_KEY);
      useSessionStore.setState({ status: 'idle' });
    }
  })();
  return resuming;
}

export async function leaveMatch(): Promise<void> {
  const { room } = useSessionStore.getState();
  if (!room) return;
  await room.leave(true);
}

/**
 * Share link for a room: opening it pre-fills the join code. Always the app root plus `?room=`,
 * whatever path this tab is on, so it resolves on any static host without a rewrite rule.
 */
export function inviteLink(roomId: string): string {
  const url = new URL(import.meta.env.BASE_URL, window.location.origin);
  url.searchParams.set('room', roomId);
  return url.toString();
}
